import React, { memo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Order } from '../../types/order';
import { FavoriteOrder } from '../../services/favoriteService';
import { LoadingSpinner } from '../ui';
import UserOrderCard from './UserOrderCard';
import FavoriteOrderCard from './FavoriteOrderCard';
import UserOrdersEmptyState from './UserOrdersEmptyState';

interface UserOrdersContentProps {
  // Data
  filteredOrders: Order[];
  favoriteOrders: FavoriteOrder[];
  favoriteIds: Set<number>;

  // State
  statusFilter: string;
  loading: boolean;
  favoritesLoading: boolean;

  // Error handling
  error: any;
  favoritesError: any;
  getEmptyStateType: () => 'error' | 'no-orders' | 'no-filtered-results' | 'no-favorites';
  getFavoritesEmptyStateType: () => 'error' | 'no-orders' | 'no-filtered-results' | 'no-favorites';
  hasFilters: boolean;

  // Handlers - Orders
  onOrderDetailClick: (order: Order) => void;
  onReorder: (order: Order) => void;
  onFavoriteToggle: (orderId: number) => void;

  // Handlers - Favorites
  onRemoveFavorite: (orderId: number) => void;
  onFavoriteReorder: (favorite: FavoriteOrder) => void;
  onFavoriteDetailClick: (favorite: FavoriteOrder) => void;
  onToggleFavoriteFromList: (orderId: number) => void;

  // Retry handlers
  onRetryOrders: () => void;
  onRetryFavorites: () => void;
}

export const UserOrdersContent = memo<UserOrdersContentProps>(
  ({
    // Data
    filteredOrders,
    favoriteOrders,
    favoriteIds,

    // State
    statusFilter,
    loading,
    favoritesLoading,

    // Error handling
    error,
    favoritesError,
    getEmptyStateType,
    getFavoritesEmptyStateType,
    hasFilters,

    // Handlers - Orders
    onOrderDetailClick,
    onReorder,
    onFavoriteToggle,

    // Handlers - Favorites
    onRemoveFavorite,
    onFavoriteReorder,
    onFavoriteDetailClick,
    onToggleFavoriteFromList,

    // Retry handlers
    onRetryOrders,
    onRetryFavorites,
  }) => {
    if (statusFilter === 'favorites') {
      // Favorites Tab Content
      if (favoritesLoading) {
        return (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600">Favori siparişleriniz yükleniyor...</p>
            </div>
          </div>
        );
      }

      if (favoritesError || favoriteOrders.length === 0) {
        return (
          <UserOrdersEmptyState
            type={getFavoritesEmptyStateType()}
            error={favoritesError}
            onRetry={onRetryFavorites}
            hasFilters={false}
          />
        );
      }

      return (
        <div className="space-y-6">
          <AnimatePresence>
            {favoriteOrders.map((favorite, index) => (
              <FavoriteOrderCard
                key={favorite.favorite_id}
                favorite={favorite}
                index={index}
                onRemove={onRemoveFavorite}
                onReorder={onFavoriteReorder}
                onViewDetails={onFavoriteDetailClick}
                onToggleFavorite={onToggleFavoriteFromList}
              />
            ))}
          </AnimatePresence>
        </div>
      );
    }

    // Normal Orders Content
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Siparişleriniz yükleniyor...</p>
          </div>
        </div>
      );
    }

    if (error || filteredOrders.length === 0) {
      return (
        <UserOrdersEmptyState
          type={getEmptyStateType()}
          error={error}
          onRetry={onRetryOrders}
          hasFilters={hasFilters}
        />
      );
    }

    return (
      <div className="space-y-6">
        <AnimatePresence>
          {filteredOrders.map((order, index) => (
            <UserOrderCard
              key={order.id}
              order={order}
              index={index}
              favorites={favoriteIds}
              onOrderDetailClick={onOrderDetailClick}
              onReorderClick={onReorder}
              onFavoriteToggle={onFavoriteToggle}
            />
          ))}
        </AnimatePresence>
      </div>
    );
  }
);

UserOrdersContent.displayName = 'UserOrdersContent';
